import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import * as Constants from "expo-constants";
import * as Device from "expo-device";
import * as Notifications from "expo-notifications";
import { useEffect, useState } from "react";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import { useSetExpoTokens } from "../../hooks/useSetExpoTokens";
import { useRegisterDeviceForPushNotificationsAsyncFragment_memberUser$key } from "./__generated__/useRegisterDeviceForPushNotificationsAsyncFragment_memberUser.graphql";

export const useRegisterDeviceForPushNotificationsAsync = async ({
  fragmentRef,
}: {
  fragmentRef: useRegisterDeviceForPushNotificationsAsyncFragment_memberUser$key | null;
}) => {
  const [setExpoTokens] = useSetExpoTokens();

  const [
    hasCheckedNotificationPermissions,
    setHasCheckedNotificationPermissions,
  ] = useState(false);

  const data = useFragment(
    useRegisterDeviceForPushNotificationsAsyncFragmentString,
    fragmentRef
  );

  const userId = data?.id || "";
  const existingTokens = (data?.metadata?.expoTokens || []).flatMap((token) =>
    token ? [token] : []
  );

  useEffect(() => {
    if (!hasCheckedNotificationPermissions) {
      setHasCheckedNotificationPermissions(true);
      const setToken = (token: string) => {
        if (!existingTokens.includes(token)) {
          setExpoTokens({ userId, tokens: [...existingTokens, token] });
        }
      };
      registerDeviceForPushNotificationsAsync(setToken).then(() => {
        Notifications.setNotificationHandler({
          handleNotification: async () => {
            return {
              shouldShowBanner: true,
              shouldShowList: true,
              shouldSetBadge: false,
              shouldShowAlert: true,
              shouldPlaySound: true,
            };
          },
        });
      });
    }
  }, [
    existingTokens,
    hasCheckedNotificationPermissions,
    setExpoTokens,
    userId,
  ]);
};

const registerDeviceForPushNotificationsAsync = async (
  setToken: (token: string) => void
) => {
  if (Device.isDevice && Platform.OS !== "web") {
    const permissions = await Notifications.getPermissionsAsync();

    const existingStatus = permissions.status;
    let finalStatus = existingStatus;
    if (existingStatus !== "granted" && existingStatus !== "denied") {
      const { status } = await Notifications.requestPermissionsAsync();
      finalStatus = status;
    }
    if (finalStatus !== "granted") {
      // User did not grant permission
      return;
    }
    const token = (
      await Notifications.getExpoPushTokenAsync({
        projectId: Constants.default.expoConfig?.extra?.eas.projectId,
      })
    ).data;
    setToken(token);

    if (Platform.OS === "android") {
      Notifications.setNotificationChannelAsync("default", {
        name: t`Default`,
        importance: Notifications.AndroidImportance.MAX,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: "#FF231F7C",
      });
    }
  }
};

const useRegisterDeviceForPushNotificationsAsyncFragmentString = graphql`
  fragment useRegisterDeviceForPushNotificationsAsyncFragment_memberUser on MemberUser {
    id
    metadata {
      expoTokens
    }
  }
`;
